// Sport-specific background images from Unsplash
const sportBackgrounds = {
  football: 'https://images.unsplash.com/photo-1762013315117-1c8005ad2b41?w=800&q=80',
  cricket: 'https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=800&q=80',
  swimming: 'https://images.unsplash.com/photo-1530549387789-4c1017266635?w=800&q=80',
  tennis: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800&q=80',
  basketball: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&q=80',
  badminton: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=800&q=80',
  athletics: 'https://images.unsplash.com/photo-1461896836934-bd45ba30b14b?w=800&q=80',
  default: 'https://images.unsplash.com/photo-1461896836934-bd45ba30b14b?w=800&q=80',
};

export function getEventBackground(eventType, sport) {
  const type = (eventType || sport || '').toLowerCase();
  for (const [key, url] of Object.entries(sportBackgrounds)) {
    if (type.includes(key)) return url;
  }
  return sportBackgrounds.default;
}

export function getQRDataUrl(text, size = 120) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&bgcolor=ffffff&color=000000&margin=4`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 202.c.2 — Translate Gemini's free-form `color_theme` and
// `layout_description` strings into actual rendering hints. The AI returns
// natural-language design suggestions (e.g. "Cinematic red and gold",
// "Bold title left, details right") which were previously ignored — every
// poster looked identical. These helpers map the suggestions to concrete
// gradient stops + accent colours + layout variants so each generation
// produces a visually distinct poster.
// ─────────────────────────────────────────────────────────────────────────────
const COLOR_LEXICON = {
  // Indian/sports-popular palette, including IPL/ISL franchise hues
  red:      '#dc2626',
  crimson:  '#dc2626',
  scarlet:  '#dc2626',
  maroon:   '#7f1d1d',
  burgundy: '#7f1d1d',
  orange:   '#f97316',
  amber:    '#f59e0b',
  gold:     '#eab308',
  yellow:   '#facc15',
  saffron:  '#f97316',
  green:    '#16a34a',
  emerald:  '#10b981',
  forest:   '#166534',
  lime:     '#84cc16',
  neon:     '#22d3ee',
  cyan:     '#06b6d4',
  teal:     '#14b8a6',
  turquoise:'#14b8a6',
  blue:     '#2563eb',
  navy:     '#1e3a8a',
  azure:    '#0ea5e9',
  royal:    '#1e40af',
  indigo:   '#4f46e5',
  violet:   '#7c3aed',
  purple:   '#9333ea',
  magenta:  '#d946ef',
  pink:     '#ec4899',
  rose:     '#f43f5e',
  silver:   '#94a3b8',
  grey:     '#475569',
  gray:     '#475569',
  black:    '#0f172a',
  white:    '#f8fafc',
  ivory:    '#fffbeb',
};

// Map "Cinematic red and gold" → { from: '#dc2626', to: '#eab308', accent: '#eab308' }
// Falls back to teal/amber if the AI string doesn't yield ≥1 recognised colour.
export function parseColorTheme(text) {
  const fallback = { from: '#0f766e', to: '#d97706', accent: '#fbbf24', text: '#ffffff' };
  if (!text || typeof text !== 'string') return fallback;
  const lower = text.toLowerCase();
  const hits = [];
  for (const [word, hex] of Object.entries(COLOR_LEXICON)) {
    // word-boundary match so "redirect" doesn't trigger red, but "red-and-gold" does
    if (new RegExp(`\\b${word}\\b`).test(lower)) {
      hits.push(hex);
      if (hits.length === 2) break;
    }
  }
  if (hits.length === 0) return fallback;
  if (hits.length === 1) return { from: hits[0], to: hits[0], accent: hits[0], text: '#ffffff' };
  return { from: hits[0], to: hits[1], accent: hits[1], text: '#ffffff' };
}

// Map "Bold title centered, details below" → 'hero-center'
//     "Split layout, headline left, image right" → 'split-left'
//     "Minimal top header, content stacked" → 'minimal-stack'
// Default: 'hero-center'
export function pickPosterLayout(text) {
  if (!text || typeof text !== 'string') return 'hero-center';
  const lower = text.toLowerCase();
  if (/\b(split|left.*right|two[\s-]column|side[\s-]by[\s-]side)\b/.test(lower)) return 'split-left';
  if (/\b(minimal|clean|stack|top header|sparse)\b/.test(lower)) return 'minimal-stack';
  return 'hero-center';
}

